import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, Grid, List, TrendingUp, SlidersHorizontal } from 'lucide-react';
import { supabase } from '../supabaseClient';
import ProductCard from '../components/ProductCard';

const Shop = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      // Fetch all products
      const { data: prods } = await supabase
        .from('products')
        .select('*, categories(name)')
        .order('created_at', { ascending: false });
      
      setProducts(prods || []);

      // Fetch categories
      const { data: cats } = await supabase
        .from('categories')
        .select('*')
        .order('name');
      
      setCategories(cats || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = React.useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('products')
        .select('*, categories(name)')
        .order('created_at', { ascending: false });

      // Filter by category
      if (selectedCategory !== 'all') {
        const category = categories.find(c => c.name.toLowerCase() === selectedCategory.toLowerCase());
        if (category) {
          query = query.eq('category_id', category.id);
        }
      }

      // Search by name or description
      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
      }

      const { data } = await query;
      let filteredData = data || [];

      // Sort products
      switch(sortBy) {
        case 'price-low':
          filteredData.sort((a, b) => a.price - b.price);
          break;
        case 'price-high':
          filteredData.sort((a, b) => b.price - a.price);
          break;
        case 'popular':
          filteredData.sort((a, b) => (b.rating || 0) - (a.rating || 0));
          break;
        case 'newest':
        default:
          // Already sorted by created_at
          break;
      }

      setProducts(filteredData);
    } catch (error) {
      console.error('Error filtering products:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchTerm, categories, sortBy]);

  useEffect(() => {
    filterProducts();
  }, [filterProducts]);

  return (
    <div className="min-h-screen py-16 relative">
      {/* Background decorative elements */}
      <div className="absolute top-40 left-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 right-10 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl"></div>
      
      <div className="section-container relative z-10">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-6">
            <TrendingUp size={16} className="text-cyan-400" />
            <span>Premium Production-Ready Solutions</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold font-poppins mb-6">
            Browse <span className="gradient-text">Products</span>
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto">
            Discover production-ready code solutions for your next project. Built with best practices, fully documented, and ready to deploy.
          </p>
        </div>

        {/* Enhanced Filters */}
        <div className="glass-card p-6 md:p-8 mb-10 rounded-2xl border border-cyan-500/10">
          <div className="space-y-4">
            {/* Search and View Toggle */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {/* Search */}
              <div className="md:col-span-8">
                <div className="relative group">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-cyan-400 transition" size={20} />
                  <input
                    type="text"
                    placeholder="Search products by name, description, or tech stack..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition"
                    data-testid="search-products-input"
                  />
                </div>
              </div>

              {/* View Mode Toggle */}
              <div className="md:col-span-4 flex items-center justify-end space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 rounded-lg transition ${viewMode === 'grid' ? 'bg-gradient-to-r from-cyan-400 to-teal-500 text-gray-900' : 'bg-gray-800/50 text-gray-400 hover:text-white'}`}
                  aria-label="Grid view"
                  data-testid="grid-view-button"
                >
                  <Grid size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 rounded-lg transition ${viewMode === 'list' ? 'bg-gradient-to-r from-cyan-400 to-teal-500 text-gray-900' : 'bg-gray-800/50 text-gray-400 hover:text-white'}`}
                  aria-label="List view"
                  data-testid="list-view-button"
                >
                  <List size={20} />
                </button>
              </div>
            </div>

            {/* Category and Sort Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category Filter */}
              <div className="relative group">
                <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-cyan-400 transition pointer-events-none" size={20} />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition appearance-none cursor-pointer"
                  data-testid="category-filter"
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name.toLowerCase()}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Filter */}
              <div className="relative group">
                <SlidersHorizontal className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-cyan-400 transition pointer-events-none" size={20} />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition appearance-none cursor-pointer"
                  data-testid="sort-filter"
                >
                  <option value="newest">Newest First</option>
                  <option value="popular">Most Popular</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Active Filters Display */}
        {(selectedCategory !== 'all' || searchTerm) && (
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <span className="text-gray-400 text-sm">Active filters:</span>
            {selectedCategory !== 'all' && (
              <span className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-full text-sm flex items-center space-x-2">
                <span>{selectedCategory}</span>
                <button onClick={() => setSelectedCategory('all')} className="hover:text-cyan-300">
                  ×
                </button>
              </span>
            )}
            {searchTerm && (
              <span className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-full text-sm flex items-center space-x-2">
                <span>Search: {searchTerm}</span>
                <button onClick={() => setSearchTerm('')} className="hover:text-cyan-300">
                  ×
                </button>
              </span>
            )}
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="text-center text-gray-400 py-32">
            <div className="relative w-20 h-20 mx-auto mb-8">
              <div className="absolute inset-0 border-4 border-cyan-500/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-xl font-semibold">Loading products...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait while we fetch the latest products</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center text-gray-400 py-32">
            <div className="glass-card max-w-md mx-auto p-12 rounded-3xl">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-400/20 to-teal-500/20 rounded-full flex items-center justify-center">
                <Search size={40} className="text-cyan-400" />
              </div>
              <p className="text-3xl font-bold mb-4">No products found</p>
              <p className="text-base text-gray-500 mb-6">Try adjusting your search or filters to find what you're looking for</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                className="gradient-button px-8 py-3 rounded-xl"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <div className="text-gray-400 text-lg">
                <span className="text-cyan-400 font-semibold text-2xl">{products.length}</span> {products.length === 1 ? 'product' : 'products'} found
              </div>
            </div>
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8' 
              : 'space-y-6'
            }>
              {products.map((product) => (
                <ProductCard key={product.id} product={product} viewMode={viewMode} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Shop;